import { useEffect, useRef, useState } from 'react';
import { Loader } from '@dreamtree-org/twreact-ui';
import { Table2, ChevronDown, Search, RefreshCw, Check } from 'lucide-react';
import { useAppApi } from '@/lib/api';

/**
 * Table scope picker, shared by both migration screens.
 *
 * Controlled. `value` = { allMode: boolean, tables: string[] }:
 *   allMode true  → every source table (the default; `tables` is ignored)
 *   allMode false → only the checked `tables`
 *
 * The table list is loaded lazily, the first time the panel opens, because it costs a
 * live introspection of the source DB and needs a usable URL. Until then the value means
 * "All tables", so Compare/Plan works without ever opening this.
 *
 * `endpoint` is the @api registry key to call — the two migration tools introspect the
 * source differently (schema vs. row catalog) and return different payloads, so the
 * caller picks. Both answer with `{ tables: string[] }`.
 */
export default function TablePicker({ sourceUrl, value, onChange, disabled, endpoint = 'dbMigrationTables' }) {
  const { fetchApi } = useAppApi();
  const { allMode, tables: selected } = value;
  const selectedSet = new Set(selected || []);

  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loadTables = async () => {
    if (!sourceUrl?.trim()) {
      setError('Enter the source DB URL first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi(endpoint, { url: sourceUrl.trim() });
      // dbMigrationTables answers { tables: [...] }; dataMigrationTables answers a
      // catalog of descriptors, so normalise to plain names either way.
      const list = (res?.tables || []).map((t) => (typeof t === 'string' ? t : t.table));
      setAvailable(list);
      setLoaded(true);
    } catch (ex) {
      setError((ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Could not load tables');
    } finally {
      setLoading(false);
    }
  };

  const togglePanel = () => {
    if (disabled) return;
    setOpen((o) => !o);
    if (!open && !loaded && !loading) loadTables();
  };

  /**
   * Toggling from all-mode seeds the selection with every known table and then removes
   * the one being unchecked — otherwise "All" minus one would collapse to a single table.
   * Re-selecting everything folds back to all-mode so no table filter is sent at all.
   */
  const toggleTable = (name) => {
    let next;
    if (allMode) {
      next = new Set(available);
      next.delete(name);
    } else {
      next = new Set(selectedSet);
      if (next.has(name)) next.delete(name);
      else next.add(name);
    }
    if (available.length && next.size === available.length) onChange({ allMode: true, tables: [] });
    else onChange({ allMode: false, tables: [...next] });
  };

  const filtered = available.filter((t) => t.toLowerCase().includes(search.toLowerCase()));
  const plural = selectedSet.size === 1 ? '' : 's';
  const count = allMode ? 'All tables' : `${selectedSet.size} table${plural}`;
  const isChecked = (t) => allMode || selectedSet.has(t);

  const box = (on) =>
    `w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
      on ? 'bg-primary-600 border-primary-600' : 'border-secondary-300 dark:border-secondary-600'
    }`;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={togglePanel}
        disabled={disabled}
        className="w-full flex items-center gap-2 px-3 py-2 border border-secondary-200 dark:border-secondary-700 rounded-lg text-sm hover:bg-secondary-50 dark:hover:bg-secondary-800 disabled:opacity-50"
      >
        <Table2 size={16} className="opacity-50" />
        <span className={allMode ? 'opacity-70' : 'font-medium'}>{count}</span>
        <ChevronDown size={16} className="ml-auto opacity-50" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-[var(--app-surface)] border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 p-2 border-b border-secondary-100 dark:border-secondary-800">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tables…"
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-transparent border border-secondary-200 dark:border-secondary-700 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>
            <button
              type="button"
              onClick={loadTables}
              title="Reload tables from source"
              className="p-1.5 opacity-50 hover:opacity-100 rounded"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => onChange({ allMode: true, tables: [] })}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary-50 dark:hover:bg-secondary-800 border-b border-secondary-100 dark:border-secondary-800"
          >
            <span className={box(allMode)}>{allMode && <Check size={12} className="text-white" />}</span>
            <span className="font-medium">All tables</span>
            <span className="ml-auto text-[11px] opacity-50">default</span>
          </button>

          {loaded && available.length > 0 && (
            <div className="flex items-center gap-3 px-3 py-1.5 text-[11px] border-b border-secondary-100 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-800/50">
              <button type="button" className="text-primary-600 hover:underline" onClick={() => onChange({ allMode: true, tables: [] })}>Select all</button>
              <button type="button" className="opacity-70 hover:underline" onClick={() => onChange({ allMode: false, tables: [] })}>Clear</button>
              <span className="ml-auto opacity-50">{available.length} tables</span>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto">
            {loading && <div className="py-4"><Loader text="Loading tables…" /></div>}
            {!loading && error && <div className="px-3 py-4 text-xs text-error-600">{error}</div>}
            {!loading && !error && !loaded && <div className="px-3 py-4 text-xs opacity-50">Open to load tables.</div>}
            {!loading && !error && loaded && filtered.length === 0 && (
              <div className="px-3 py-4 text-xs opacity-50">No tables match “{search}”.</div>
            )}
            {!loading && !error && loaded && filtered.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleTable(t)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-secondary-50 dark:hover:bg-secondary-800 text-left"
              >
                <span className={box(isChecked(t))}>{isChecked(t) && <Check size={12} className="text-white" />}</span>
                <span className="font-mono text-xs truncate">{t}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
